import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

export type DebugTarget = {
    id: string;
    url: string;
    text: string;
    debug?: {
        props: { data?: any; dynamicSectionsData?: any; layer?: any };
        title: string;
    };
};

type DebugTargetsApi = {
    register: (key: string, targets: DebugTarget[]) => void;
    unregister: (key: string) => void;
};

// The api sits in its own context so registrants subscribe to a value that never
// changes: re-rendering them on every registry update would feed straight back
// into the effect below and loop.
const DebugTargetsApiContext = createContext<DebugTargetsApi>({
    register: () => undefined,
    unregister: () => undefined,
});
const DebugTargetsStateContext = createContext<DebugTarget[]>([]);

export function DebugTargetsProvider({ children }: { children: React.ReactNode }) {
    const [registry, setRegistry] = useState<Map<string, DebugTarget[]>>(new Map());

    const api = useMemo<DebugTargetsApi>(() => ({
        register: (key, targets) => setRegistry((prev) => new Map(prev).set(key, targets)),
        unregister: (key) => setRegistry((prev) => {
            if (!prev.has(key)) {
                return prev;
            }
            const next = new Map(prev);
            next.delete(key);
            return next;
        }),
    }), []);

    const targets = useMemo(() => Array.from(registry.values()).flat(), [registry]);

    return (
        <DebugTargetsApiContext.Provider value={api}>
            <DebugTargetsStateContext.Provider value={targets}>
                {children}
            </DebugTargetsStateContext.Provider>
        </DebugTargetsApiContext.Provider>
    );
}

/**
 * Register debug targets for the current page. Targets are automatically
 * unregistered when the component unmounts (i.e. when navigating away).
 *
 * Re-registers whenever a target's identity or the live object behind its
 * `debug.props` changes, so a modal shows current state instead of whatever was
 * there on first render. Keep those props referentially stable (a redux object,
 * or `useMemo` for an assembled one) or every render re-registers.
 *
 * @param key    Stable identifier for this registrant (e.g. "pdf_report").
 * @param targets Array of DebugTarget objects. Pass an empty array or omit to
 *               suppress registration (useful when the config id isn't resolved yet).
 */
export function useRegisterDebugTargets(key: string, targets: DebugTarget[]) {
    const { register, unregister } = useContext(DebugTargetsApiContext);
    const registered = useRef<unknown[] | null>(null);

    // Diffed by reference on every render. Never serialise the targets: callers
    // hand us live app objects (a layer, a check) that carry circular references,
    // and a JSON.stringify over those throws during render, taking the whole page
    // down with it.
    useEffect(() => {
        const fingerprint = fingerprintOf(targets);
        if (registered.current && isSameFingerprint(registered.current, fingerprint)) {
            return;
        }
        registered.current = fingerprint;

        if (targets.length > 0) {
            register(key, targets);
        } else {
            unregister(key);
        }
    });

    useEffect(() => () => unregister(key), [key, unregister]);
}

/** Read all currently registered debug targets (used by AdminBanner in Footer). */
export function useDebugTargets(): DebugTarget[] {
    return useContext(DebugTargetsStateContext);
}

function fingerprintOf(targets: DebugTarget[]): unknown[] {
    return targets.flatMap((target) => [
        target.id,
        target.url,
        target.text,
        target.debug?.title,
        ...(target.debug ? Object.values(target.debug.props) : []),
    ]);
}

function isSameFingerprint(a: unknown[], b: unknown[]): boolean {
    return a.length === b.length && a.every((value, index) => value === b[index]);
}
